import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static values = { siteKey: { type: String, default: "" } }
  static targets = ["button", "container", "input"]

  connect() {
    if (this.siteKeyValue === "") {
      return
    }

    this.pendingSubmit = false

    if (!window.turnstile) {
      this.loadTurnstileScript()
    }

    this.element.addEventListener("submit", (event) => {
      if (this.inputTarget.value === "") {
        event.preventDefault()
        this.initTurnstile()
      }
    })
  }

  loadTurnstileScript() {
    const script = document.createElement("script")
    script.src = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit&onload=onTurnstileLoad"
    script.defer = "defer"
    script.async = true

    window.onTurnstileLoad = () => {
      if (this.pendingSubmit) {
        this.pendingSubmit = false
        this.initTurnstile()
      }
    }
    document.body.appendChild(script)
  }

  disconnect() {
    this.clearTimeoutTimer()
    if (window.turnstile && this.widgetId) {
      turnstile.remove(this.widgetId)
    }
  }

  disableButton() {
    this.buttonTarget.disabled = true
    this.buttonTarget.classList.add("cursor-not-allowed", "opacity-50")
  }

  enableButton() {
    this.buttonTarget.disabled = false
    this.buttonTarget.classList.remove("cursor-not-allowed", "opacity-50")
  }

  initTurnstile() {
    if (!window.turnstile) {
      this.pendingSubmit = true
      this.disableButton()
      this.scheduleTimeout()
      return
    }

    this.disableButton()
    this.clearTimeoutTimer()

    if (this.widgetId) {
      turnstile.reset(this.widgetId)
      return
    }

    this.widgetId = turnstile.render(this.containerTarget, {
      sitekey: this.siteKeyValue,
      theme: "light",
      size: "flexible",
      "error-callback": (errorCode) => {
        console.error("Turnstile error:", errorCode)
        this.handleTurnstileError(errorCode)
      },
      "expired-callback": () => {
        this.inputTarget.value = ""
        if (this.widgetId) {
          turnstile.reset(this.widgetId)
        }
      },
      "timeout-callback": () => {
        this.handleTurnstileError()
      },
      "unsupported-callback": () => {
        this.enableButton()
        App.displayFlash("warning", "Verification unavailable. You may proceed.")
      },
      "before-interactive-callback": () => {
        setTimeout(() => {
          App.displayFlash("warning", `Proceed with the verification to confirm you are human.`)
        }, 1000)
        this.disableButton()
        this.containerTarget.classList.remove("hidden")
      },
      "after-interactive-callback": () => {
        this.containerTarget.classList.add("hidden")
        this.enableButton()
      },
      callback: (token) => {
        this.clearTimeoutTimer()
        this.inputTarget.value = token
        if (this.element.requestSubmit) {
          this.element.requestSubmit(this.buttonTarget)
        } else {
          this.element.submit()
        }
      },
    })
  }

  scheduleTimeout() {
    this.clearTimeoutTimer()
    this.timeoutId = setTimeout(() => {
      this.handleTurnstileError()
    }, 15000)
  }

  clearTimeoutTimer() {
    if (this.timeoutId) {
      clearTimeout(this.timeoutId)
      this.timeoutId = null
    }
  }

  handleTurnstileError(errorCode = null) {
    this.enableButton()
    this.clearTimeoutTimer()
    this.pendingSubmit = false

    const message = errorCode
      ? `Turnstile verification failed (${errorCode}). Please try again.`
      : "Turnstile verification failed. Please try again."
    App.displayFlash("error", message)

    if (window.turnstile && this.widgetId) {
      turnstile.reset(this.widgetId)
    }
  }
}
